import type { AsyncData, UseFetchOptions } from '#app'
import { buildRequestInit } from '@datocms/cda-client'
import type { TadaDocumentNode } from 'gql.tada'
import { hash } from 'ohash'
import { useQuerySubscription } from 'vue-datocms'

const isServer = typeof window === 'undefined'
// TODO: Fix types!
type Options<Variables> = {
  variables?: Variables & UseFetchOptions
}

export async function useDatoQuery<Result, Variables>(
  query: TadaDocumentNode<Result, Variables>,
  options?: Options<Variables> & UseFetchOptions
) {
  const config = useRuntimeConfig()
  const draftMode = useDraftMode()
  // console.log('ISS useDatoQuery draftMode:', draftMode)
  /*
   * Depending on whether the Draft Mode is active or not, we use an API token
   * with different permissions
   */
  const apiToken = draftMode
    ? // If Draft mode is ON, then we use the token saved in the cookie, which (if
      // not forged) allows us to access the contents in draft
      draftMode.datocmsDraftContentCdaToken
    : // Otherwise, we use the token to access the published content. Given that
      // this token does not expose sensitive information, it is also accessible
      // on the client side.
      config.public.datocmsPublishedContentCdaToken

  if (!apiToken) {
    throw new Error('Missing API token')
  }

  const environmentId = config.public.datocmsEnvironmentId

  const opts = options || {}
  const { variables, ...fetchOptions } = opts as Options<Variables> & UseFetchOptions
  // console.log('useDatoQuery useFetchOptions:', fetchOptions)

  const initialData = useFetch('https://graphql.datocms.com/', {
    ...buildRequestInit(query, {
      token: apiToken,
      variables: options?.variables,
      includeDrafts: Boolean(draftMode),
      excludeInvalid: true,
      environment: environmentId
    }),
    ...fetchOptions,
    key: hash([query, options]),
    transform: ({ data, errors }) => {
      if (errors)
        throw new Error(`Something went wrong while executing the query: ${JSON.stringify(errors)}`)

      return data
    }
  }) as AsyncData<Result, null>

  // If the Draft Mode is off, or if it is active but the composable is run
  // server-side, we simply return the result of the query.
  if (!draftMode || isServer || process.env.NUXT_PUBLIC_ENV === 'production') {
    return initialData
  }

  // If we are in Draft Mode instead, and the composable is run client-side,
  // then we initiate the connection with the DatoCMS Real-time Updates API.
  return useQuerySubscription<Result, Variables>({
    query,
    variables: options?.variables,
    token: apiToken,
    initialData: (await initialData).data.value,
    includeDrafts: true,
    excludeInvalid: true
  })
}
